import {
  faArrowsRotate,
  faLocationCrosshairs,
  faMagnifyingGlassLocation,
  faMapLocationDot,
  faSuitcaseRolling,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { parseAsFloat, parseAsInteger, parseAsString, useQueryState } from 'nuqs';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapboxMap, {
  Layer,
  type MapRef,
  Marker,
  NavigationControl,
  Popup,
  Source,
} from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { type DirectionsLineString, fetchDirections } from '@/api/directions';
import { useTripContext } from '@/contexts/TripContext';
import type { ItineraryItem } from '@/types/itinerary';
import type { Trip, TripPlace } from '@/types/trip';
import { categoryDefinitions } from '@/utils/categories';
import {
  type BBox,
  createClusterer,
  formatClusterCount,
  isNeverClustered,
} from '@/utils/mapClusters';
import { twx } from '@/utils/tailwind';

export interface ItineraryMapProps {
  items: Array<ItineraryItem>;
  /** All search-result items before category filtering — used to determine which trip places fall outside the search area */
  allItems: Array<ItineraryItem>;
  lat: number;
  lng: number;
  radiusMiles: number;
  selectedTrip?: Trip | null;
  showRoutes?: boolean;
  /** Travel mode: only show trip places for the active day; hide search results entirely */
  travelMode?: boolean;
  /** When true, non-trip pins become HTML5-draggable so they can be
   *  dropped onto a registered drop target (ScoutedList Scouted section).
   *  Touch devices ignore this flag (pointer-coarse detection) since
   *  HTML5 drag-and-drop doesn't behave well with touch. */
  enableDrag?: boolean;
  /** A places search is in flight (initial fetch or background ingest).
   *  Drives the "Finding places…" state of the search-area chip. */
  isLoading?: boolean;
  /** Fires when the user right-clicks (or long-presses) the map
   *  background. Used to prompt for a custom pin at that location. The
   *  parent owns the modal — the map just emits the coordinate. */
  onMapContextMenu?: (coords: { latitude: number; longitude: number }) => void;
}

const radiusToZoom = (miles: number): number => {
  if (miles <= 5) return 12;
  if (miles <= 10) return 11;
  if (miles <= 25) return 10;
  return 9;
};

const zoomToRadius = (zoom: number): number => {
  if (zoom >= 12) return 5;
  if (zoom >= 11) return 10;
  if (zoom >= 10) return 20;
  return 30;
};

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

const pinIconStyle = (size: number): FAIconStyle =>
  ({
    width: size,
    height: size,
    '--fa-primary-color': 'white',
    '--fa-secondary-color': 'rgba(255,255,255,0.85)',
  }) as FAIconStyle;

/** Size + ring classes for the circular non-trip marker (search results).
 * Trip places use the heart-shaped variant below and don't go through this
 * helper. */
const markerStateClasses = (
  isSelected: boolean,
): { sizeClass: string; ringClass: string; iconSize: number } => {
  if (isSelected) {
    return {
      sizeClass: 'w-10 h-10 scale-110',
      ringClass: 'ring-2 ring-secondary-emphasized ring-offset-1 border-white',
      iconSize: 16,
    };
  }
  return {
    sizeClass: 'w-7 h-7 hover:scale-110',
    ringClass: 'border-white',
    iconSize: 12,
  };
};

/** SVG-stroke color (CSS variable) for the heart outline on trip markers.
 * We resolve to actual color values because the SVG `stroke` attribute
 * doesn't accept Tailwind classes. */
const tripPlaceTypeStroke: Record<string, string> = {
  activity: 'var(--color-secondary-emphasized)',
  lodging: 'var(--color-tertiary-emphasized)',
  convenience: 'var(--color-quaternary-emphasized)',
};

/** Symmetric heart path centered around (50,50) inside a 100×100 viewBox. */
const HEART_PATH =
  'M 50 86 C 50 86, 8 56, 8 30 C 8 14, 22 5, 35 10 C 42 13, 47 18, 50 26 C 53 18, 58 13, 65 10 C 78 5, 92 14, 92 30 C 92 56, 50 86, 50 86 Z';

// Per-marker memoized component — extracted so the parent re-rendering doesn't
// re-create every marker's JSX. The marker only re-renders when its own props
// change (selection state, trip-ring state, callbacks via memoized parents).
/** Optional staggered drop-in animation for pins entering the viewport.
 *  Caller passes a per-marker delay in ms — the parent loop typically
 *  derives it from the marker's index in the result set. */
interface MapMarkerEntranceProps {
  entranceDelayMs?: number;
}

interface MapMarkerProps extends MapMarkerEntranceProps {
  item: ItineraryItem;
  isSelected: boolean;
  tripPlaceType: TripPlace['placeType'] | null;
  onClick: (id: string) => void;
  onHover: (id: string) => void;
  onUnhover: () => void;
  /** When true, the marker button is HTML5-draggable. The drag payload
   *  sets `application/x-trippy-place-id` to the item's id so a drop
   *  zone (currently the ScoutedList Scouted section) can look up + add
   *  the place. Trip pins set this to false from the parent (already in
   *  the trip — nothing to add). */
  draggable?: boolean;
}

const MapMarkerImpl = ({
  item,
  isSelected,
  tripPlaceType,
  onClick,
  onHover,
  onUnhover,
  entranceDelayMs = 0,
  draggable = false,
}: MapMarkerProps) => {
  // Custom pointer-event-based drag (NOT HTML5 DnD). Mapbox's capture-
  // phase pointer handler calls preventDefault on pointerdown which
  // would kill the browser's HTML5 drag system before it ever starts a
  // drag. By implementing drag ourselves with pointer events we sidestep
  // that entirely: we stopPropagation so Mapbox never starts a pan, then
  // track pointermove on the document to detect the drag and follow the
  // cursor with our own preview, and pointerup to detect drop.
  //
  // Communication with drop zones is via window-level CustomEvents:
  //   - `trippy-place-drag-over` — fires on every move with the current
  //     drop-zone id (or null) so zones can toggle hover state.
  //   - `trippy-place-dropped`  — fires on a successful drop with the
  //     item id + drop-zone id. The drop zone's listener decides what
  //     to do with the payload.
  //   - `trippy-place-drag-end`  — fires after every drag completes,
  //     successful or not, so zones can clear hover state.
  const handleDragPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return; // left-click only
    // stopPropagation: blocks the pointerdown from bubbling to Mapbox's
    // map-container listener. preventDefault: suppresses the browser's
    // compat `mousedown`/`mouseup`/`click` synthesized from this pointer
    // gesture — Mapbox's DragPan handler uses `mousedown`, and without
    // this it'd still see the event and start a pan even after our
    // stopPropagation. The click handler on the button is also
    // suppressed as a result — we fire `onClick` ourselves in `onUp`
    // when the user didn't actually drag (dead-zone tap).
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    let isDragging = false;
    let preview: HTMLDivElement | null = null;
    let currentZone: string | null = null;

    const ensurePreview = () => {
      if (preview) return preview;
      // Merged pin + name pill — visually mirrors the marker so the user
      // sees what they're carrying. Left half: a colored disc in the
      // place's category color with the category's FontAwesome icon
      // inlined as SVG. Right half: the place name. Single outer
      // capsule with border + shadow so it reads as one object.
      const el = document.createElement('div');
      el.style.cssText =
        'position:fixed;top:0;left:0;pointer-events:none;z-index:9999;display:inline-flex;align-items:center;gap:8px;background:white;border:2px solid var(--color-secondary-emphasized);border-radius:9999px;padding:4px 12px 4px 4px;font-size:13px;font-weight:500;color:var(--color-content-text);box-shadow:0 4px 14px rgba(50,20,80,0.18);transform:translate(-50%, -50%);white-space:nowrap;max-width:280px;';

      // Build the category disc — colored circle with the duotone icon
      // inlined. FontAwesome's IconDefinition exposes its raw SVG via
      // `def.icon.icon = [w, h, ligatures, unicode, paths]`. Duotone
      // icons store paths as `[primaryPath, secondaryPath]`; solid/
      // regular as a single string. We render the secondary path with
      // reduced opacity (the FA duotone convention) under the primary.
      const pinSize = 28;
      const pin = document.createElement('span');
      pin.style.cssText = `display:inline-flex;align-items:center;justify-content:center;width:${pinSize}px;height:${pinSize}px;border-radius:9999px;background:${def.pinColor};border:2px solid white;box-shadow:0 1px 2px rgba(0,0,0,0.15);flex-shrink:0;color:white;`;
      const [iconW, iconH, , , rawPath] = def.icon.icon as [
        number,
        number,
        Array<string>,
        string,
        string | Array<string>,
      ];
      const paths = Array.isArray(rawPath) ? rawPath : [rawPath];
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', `0 0 ${iconW} ${iconH}`);
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      svg.style.cssText = 'width:60%;height:60%;color:white;';
      if (paths.length > 1 && paths[1]) {
        // Secondary (background) layer — drawn first so primary sits
        // on top. Reduced opacity matches FA's duotone defaults.
        const secondary = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        secondary.setAttribute('d', paths[1]);
        secondary.setAttribute('fill', 'currentColor');
        secondary.setAttribute('opacity', '0.6');
        svg.appendChild(secondary);
      }
      const primary = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      primary.setAttribute('d', paths[0] ?? '');
      primary.setAttribute('fill', 'currentColor');
      svg.appendChild(primary);
      pin.appendChild(svg);

      const name = document.createElement('span');
      name.textContent = item.name;
      name.style.cssText = 'overflow:hidden;text-overflow:ellipsis;line-height:1.1;';
      el.appendChild(pin);
      el.appendChild(name);
      document.body.appendChild(el);
      preview = el;
      return el;
    };

    const hitTestZone = (x: number, y: number): string | null => {
      const el = document.elementFromPoint(x, y);
      const zone = (el as Element | null)?.closest('[data-drop-zone]');
      return zone?.getAttribute('data-drop-zone') ?? null;
    };

    let lastTrailAt = 0;
    const spawnTrail = (x: number, y: number) => {
      const dot = document.createElement('div');
      // Reuse the marker's category color so the trail visually ties
      // back to the place being dragged.
      dot.style.cssText = `position:fixed;left:${x}px;top:${y}px;width:6px;height:6px;border-radius:9999px;pointer-events:none;z-index:9998;background:${def.pinColor};transform:translate(-50%,-50%);opacity:0.55;animation:kawaii-trail-fade 0.6s ease-out forwards;`;
      document.body.appendChild(dot);
      dot.addEventListener('animationend', () => dot.remove(), { once: true });
    };

    const onMove = (ev: PointerEvent) => {
      const moved = Math.hypot(ev.clientX - startX, ev.clientY - startY);
      if (!isDragging) {
        if (moved < 4) return; // dead-zone so a click doesn't read as drag
        isDragging = true;
      }
      const p = ensurePreview();
      p.style.left = `${ev.clientX}px`;
      p.style.top = `${ev.clientY}px`;
      // Throttled trail spawn — ~16fps gives a continuous-feeling trail
      // without flooding the DOM with dot nodes.
      const now = ev.timeStamp || Date.now();
      if (now - lastTrailAt > 60) {
        lastTrailAt = now;
        spawnTrail(ev.clientX, ev.clientY);
      }
      const zone = hitTestZone(ev.clientX, ev.clientY);
      if (zone !== currentZone) {
        currentZone = zone;
        window.dispatchEvent(
          new CustomEvent('trippy-place-drag-over', { detail: { dropZoneId: zone } }),
        );
      }
    };

    const onUp = (ev: PointerEvent) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
      if (preview) {
        document.body.removeChild(preview);
        preview = null;
      }
      if (isDragging) {
        const zone = hitTestZone(ev.clientX, ev.clientY);
        if (zone) {
          window.dispatchEvent(
            new CustomEvent('trippy-place-dropped', {
              detail: { itemId: item.id, dropZoneId: zone },
            }),
          );
        }
        window.dispatchEvent(new CustomEvent('trippy-place-drag-end'));
      } else {
        // No drag occurred — treat as a tap and fire the click. The
        // button's onClick is suppressed for draggable markers (see
        // preventDefault on pointerdown), so we drive selection here.
        onClick(item.id);
      }
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
  };
  const def = categoryDefinitions[item.appCategory];

  // Trip-place markers: heart-shaped with a dotted trip-type outline.
  // Always render above non-trip markers (z-index 50+) so a cluster of
  // search results doesn't obscure the places you've already collected.
  // The heart shape + dotted outline both persist whether selected or not;
  // selection just bumps the scale and brings the marker further forward.
  if (tripPlaceType) {
    const strokeColor = tripPlaceTypeStroke[tripPlaceType] ?? 'currentColor';
    const heartSize = isSelected ? 44 : 36;
    const iconPx = isSelected ? 14 : 12;
    return (
      <Marker
        longitude={item.longitude}
        latitude={item.latitude}
        anchor="center"
        // Trip places always sit above regular search-result pins (zIndex 50+).
        // Selected adds another 50 to outrank other trip markers.
        style={{ zIndex: 50 + (isSelected ? 50 : 0) }}
      >
        <button
          type="button"
          // Suppress the native click when the marker is draggable —
          // preventDefault on pointerdown blocks click from firing
          // anyway, and `handleDragPointerDown`'s pointerup branch
          // calls `onClick(item.id)` itself for the dead-zone tap case.
          // Leaving both wired would double-fire selection in the
          // non-draggable code path.
          onClick={draggable ? undefined : () => onClick(item.id)}
          onMouseEnter={() => onHover(item.id)}
          onMouseLeave={onUnhover}
          title={item.name}
          aria-label={`${def.label}: ${item.name}`}
          onPointerDown={draggable ? handleDragPointerDown : undefined}
          className={twx(
            'relative block transition-transform duration-150 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-emphasized focus-visible:ring-offset-1',
            isSelected ? 'scale-110' : '',
            draggable ? 'cursor-grab active:cursor-grabbing' : '',
          )}
          style={{
            width: heartSize,
            height: heartSize,
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
          }}
        >
          {/* The heart geometry. The outer (dashed) heart sits slightly larger
              than the filled heart so the outline reads as a clear "ring"
              around the marker rather than crowding the fill. */}
          <svg
            viewBox="0 0 100 100"
            className="absolute inset-0 w-full h-full overflow-visible"
            aria-hidden
          >
            <title>Heart marker</title>
            {/* Solid trip-type outline — scaled larger around the heart's
                center so it reads as a clear ring around the filled heart. */}
            <path
              d={HEART_PATH}
              fill="none"
              stroke={strokeColor}
              strokeWidth="6"
              strokeLinejoin="round"
              transform="translate(50 50) scale(1.18) translate(-50 -50)"
            />
            {/* Filled heart with white border for separation from the map. */}
            <path d={HEART_PATH} fill={def.pinColor} stroke="white" strokeWidth="3" />
          </svg>
          {/* Place-type icon positioned slightly below the heart's
              geometric center so it sits in the middle of the lobes
              rather than up in the cleft. */}
          <FontAwesomeIcon
            icon={def.icon}
            style={
              {
                width: iconPx,
                height: iconPx,
                position: 'absolute',
                top: '47%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                '--fa-primary-color': 'white',
                '--fa-secondary-color': 'rgba(255,255,255,0.85)',
              } as FAIconStyle
            }
          />
        </button>
      </Marker>
    );
  }

  // Default: circular pin for non-trip places (search results).
  const { sizeClass, ringClass, iconSize } = markerStateClasses(isSelected);
  return (
    <Marker
      longitude={item.longitude}
      latitude={item.latitude}
      anchor="center"
      style={{ zIndex: isSelected ? 100 : 'auto' }}
    >
      <div
        className="relative motion-safe:animate-kawaii-pin-drop"
        style={{ animationDelay: `${entranceDelayMs}ms` }}
      >
        {/* Focus halo — two outward ring pulses, only when selected. They
            sit behind the pin (pointer-events-none) and use the same
            category color so the halo reads as a "this is the one" cue. */}
        {isSelected && (
          <>
            <span
              aria-hidden
              className="absolute inset-0 rounded-full pointer-events-none motion-safe:animate-kawaii-pin-halo"
              style={{
                backgroundColor: def.pinColor,
                animationIterationCount: 'infinite',
                animationDuration: '1.4s',
              }}
            />
            <span
              aria-hidden
              className="absolute inset-0 rounded-full pointer-events-none motion-safe:animate-kawaii-pin-halo"
              style={{
                backgroundColor: def.pinColor,
                animationIterationCount: 'infinite',
                animationDuration: '1.4s',
                animationDelay: '0.5s',
              }}
            />
          </>
        )}
        <button
          type="button"
          // Suppress the native click when the marker is draggable —
          // preventDefault on pointerdown blocks click from firing
          // anyway, and `handleDragPointerDown`'s pointerup branch
          // calls `onClick(item.id)` itself for the dead-zone tap case.
          // Leaving both wired would double-fire selection in the
          // non-draggable code path.
          onClick={draggable ? undefined : () => onClick(item.id)}
          onMouseEnter={() => onHover(item.id)}
          onMouseLeave={onUnhover}
          title={item.name}
          onPointerDown={draggable ? handleDragPointerDown : undefined}
          className={twx(
            'relative flex items-center justify-center rounded-full border-2 shadow-md',
            'transition-all duration-150 focus:outline-none',
            'focus-visible:ring-2 focus-visible:ring-secondary-emphasized focus-visible:ring-offset-1',
            sizeClass,
            ringClass,
            draggable ? 'cursor-grab active:cursor-grabbing' : '',
          )}
          style={{ backgroundColor: def.pinColor }}
          aria-label={`${def.label}: ${item.name}`}
        >
          <FontAwesomeIcon icon={def.icon} style={pinIconStyle(iconSize)} />
        </button>
      </div>
    </Marker>
  );
};

const MapMarker = memo(MapMarkerImpl);

interface ClusterMarkerProps {
  clusterId: number;
  longitude: number;
  latitude: number;
  count: number;
  onExpand: (clusterId: number, longitude: number, latitude: number) => void;
}

/** A count bubble standing in for a dense group of search-result pins. Clicking
 *  flies the map to the zoom where the group breaks apart. Bubble grows with
 *  density (log-scaled) so a 1000-cluster reads bigger than a 12-cluster without
 *  dwarfing the map. */
const ClusterMarkerImpl = ({
  clusterId,
  longitude,
  latitude,
  count,
  onExpand,
}: ClusterMarkerProps) => {
  const size = Math.round(Math.min(58, 34 + Math.log10(Math.max(count, 1)) * 12));
  return (
    <Marker longitude={longitude} latitude={latitude} anchor="center">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onExpand(clusterId, longitude, latitude);
        }}
        aria-label={`${count} places here — zoom in to see them`}
        className="flex items-center justify-center rounded-full bg-secondary-emphasized text-white font-bold border-2 border-white shadow-md ring-4 ring-secondary-emphasized/20 transition-transform motion-safe:hover:scale-110 motion-safe:active:scale-90"
        style={{ width: size, height: size, fontSize: count >= 1000 ? 13 : 15 }}
      >
        {formatClusterCount(count)}
      </button>
    </Marker>
  );
};

const ClusterMarker = memo(ClusterMarkerImpl);

// Zoom past which search pins never cluster — beyond this they're always
// individual markers. Kept modest so zooming in to inspect an area gives real
// pins (clusters are only useful zoomed out); viewport-culling keeps the
// declustered count cheap since only visible pins render.
const CLUSTER_MAX_ZOOM = 14;

const mapStyles = [
  { id: 'mapbox://styles/mapbox/outdoors-v12', label: 'Outdoors' },
  { id: 'mapbox://styles/mapbox/streets-v12', label: 'Streets' },
  { id: 'mapbox://styles/mapbox/satellite-streets-v12', label: 'Satellite' },
] as const;

type MapStyleId = (typeof mapStyles)[number]['id'];

const searchChipBase =
  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg bg-white border border-content-border text-content-text';

interface SearchAreaChipProps {
  /** A search is in flight (initial, background ingest, or just-triggered). */
  isLoading: boolean;
  /** The user panned to a viewport that hasn't been searched yet. */
  hasNewArea: boolean;
  /** Count of places currently shown — the informational idle state. */
  placeCount: number;
  onSearch: () => void;
}

/** The map's search-area chip. Always visible, with three states:
 *  1. loading → "Finding places…" with a slowly-spinning refresh icon
 *  2. new area panned into view → "Search this area", pinging, click to search
 *  3. idle → "N places", informational only. */
const SearchAreaChip = ({ isLoading, hasNewArea, placeCount, onSearch }: SearchAreaChipProps) => {
  if (isLoading) {
    return (
      <div className={searchChipBase}>
        <FontAwesomeIcon
          icon={faArrowsRotate}
          className="motion-safe:animate-spin"
          style={{ width: 12, height: 12, animationDuration: '2s' }}
        />
        Finding places…
      </div>
    );
  }
  if (hasNewArea) {
    return (
      <button
        type="button"
        onClick={onSearch}
        className={twx(
          searchChipBase,
          'pointer-events-auto transition-colors hover:bg-content-background hover:border-secondary-main',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-emphasized',
        )}
      >
        <span className="relative inline-flex items-center justify-center">
          {/* Gentle ping behind the icon to telegraph "there's something new
              here to search". */}
          <span
            aria-hidden={true}
            className="pointer-events-none absolute -inset-1 rounded-full bg-secondary-main/40 motion-safe:animate-ping"
          />
          <FontAwesomeIcon
            icon={faMagnifyingGlassLocation}
            className="relative"
            style={{ width: 12, height: 12 }}
          />
        </span>
        Search this area
      </button>
    );
  }
  return (
    <div className={searchChipBase}>
      <FontAwesomeIcon icon={faMapLocationDot} style={{ width: 12, height: 12 }} />
      {placeCount} {placeCount === 1 ? 'place' : 'places'}
    </div>
  );
};

const ItineraryMapImpl = ({
  items,
  allItems,
  lat,
  lng,
  radiusMiles,
  selectedTrip,
  showRoutes = true,
  travelMode = false,
  enableDrag = false,
  isLoading = false,
  onMapContextMenu,
}: ItineraryMapProps) => {
  // Pointer-fine = mouse / trackpad (vs. coarse = touchscreen). HTML5
  // drag-and-drop is mouse-only in practice; we don't try to polyfill
  // touch DnD here, just hide the affordance on touch devices.
  const [isPointerFine, setIsPointerFine] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(pointer: fine)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(pointer: fine)');
    const onChange = (e: MediaQueryListEvent) => setIsPointerFine(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const canDrag = enableDrag && isPointerFine;
  const [selectedItemId, setSelectedItemId] = useQueryState('item', parseAsString);
  const [, setLat] = useQueryState('lat', parseAsFloat);
  const [, setLng] = useQueryState('lng', parseAsFloat);
  const [, setRadius] = useQueryState('radius', parseAsInteger.withDefault(10));
  const { isPanelOpen } = useTripContext();
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isProgrammaticMoveRef = useRef(false);
  const [showSearchHere, setShowSearchHere] = useState(false);
  const [activeStyleId, setActiveStyleId] = useState<MapStyleId>(mapStyles[0].id);
  const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);
  // Current map viewport (bbox + zoom), mirrored for clustering. Updated on load
  // + move-end rather than per-frame: markers are geo-anchored so they pan with
  // the map during a gesture, and we only need to re-cluster once it settles.
  const [clusterView, setClusterView] = useState<{ zoom: number; bbox: BBox } | null>(null);
  const syncClusterView = useCallback(() => {
    const map = mapRef.current?.getMap();
    const bounds = map?.getBounds();
    if (!(map && bounds)) return;
    setClusterView({
      zoom: map.getZoom(),
      bbox: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
    });
  }, []);

  // Force a resize when the trip panel opens/closes — the parent flex layout changes
  // and Mapbox's ResizeObserver doesn't always catch the unmount-driven size change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run only when isPanelOpen changes
  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.resize(), 220);
    return () => clearTimeout(t);
  }, [isPanelOpen]);

  // Watch the container for any size change and ask Mapbox to resize. Catches
  // sibling collapses (e.g. desktop list pane toggle) that don't go through
  // isPanelOpen — Mapbox's own resize observer misses these reliably under
  // CSS transitions. Debounced via rAF so a transitioning flex layout settles
  // before we measure.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => mapRef.current?.resize());
    });
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  // Items ref so the flyTo effect below can look up the selected item without
  // depending on `items` (which changes on every fresh search and would
  // otherwise trigger spurious flyTo animations).
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Trip-place items are clickable markers too, and in travel mode they're the
  // *only* markers (`items` is empty). Keep a ref of them so the flyTo effect
  // can pan to a clicked trip pin instead of falling through to the trip center.
  const tripPlaceItemsRef = useRef<Array<ItineraryItem>>([]);
  tripPlaceItemsRef.current = (selectedTrip?.places ?? []).map((place) => place.item);

  // Direct trip-switch flyTo. Fires whenever the *trip id* changes
  // (not the trip object — adding a place mutates the object but not
  // the id, so this doesn't yank on mutations). Bypasses the
  // URL-state → setLat/setLng → prop-driven flyTo chain because in
  // practice that chain was missing the trip-switch animation in
  // some scenarios (likely a stale-closure or nuqs update-batching
  // issue). Reading `selectedTrip.lat/lng` here is authoritative —
  // it's the trip object's stored coords, not the URL state. The
  // search-area flyTo below still runs too, but they'll coalesce
  // visually since both fire in the same commit.
  const lastFlownTripIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedTrip) {
      lastFlownTripIdRef.current = null;
      return;
    }
    if (selectedTrip.id === lastFlownTripIdRef.current) return;
    lastFlownTripIdRef.current = selectedTrip.id;
    if (selectedTrip.lat == null || selectedTrip.lng == null) return;
    isProgrammaticMoveRef.current = true;
    mapRef.current?.flyTo({
      center: [selectedTrip.lng, selectedTrip.lat],
      zoom: radiusToZoom(radiusMiles),
      duration: 600,
    });
  }, [selectedTrip, radiusMiles]);

  // Single coalesced flyTo — picks one target per render so two effects firing
  // in the same tick (e.g., trip selection updates lat/lng AND selectedItemId)
  // don't queue overlapping animations. Priority: selected item > search area.
  useEffect(() => {
    isProgrammaticMoveRef.current = true;
    setShowSearchHere(false);
    const selectedItem = selectedItemId
      ? (itemsRef.current.find((i) => i.id === selectedItemId) ??
        tripPlaceItemsRef.current.find((i) => i.id === selectedItemId))
      : null;
    if (selectedItem) {
      // Pan to the clicked pin. Don't zoom OUT — if the user has
      // zoomed in past our default ensure-visible level, respect that;
      // only nudge zoom up to the floor when they're zoomed further
      // out than makes sense for inspecting a single place.
      const currentZoom = mapRef.current?.getZoom() ?? 0;
      const minZoom = Math.max(13, radiusToZoom(radiusMiles));
      mapRef.current?.flyTo({
        center: [selectedItem.longitude, selectedItem.latitude],
        zoom: Math.max(currentZoom, minZoom),
        duration: 400,
      });
    } else {
      mapRef.current?.flyTo({
        center: [lng, lat],
        zoom: radiusToZoom(radiusMiles),
        duration: 600,
      });
    }
  }, [lat, lng, radiusMiles, selectedItemId]);

  // Build a lookup of item IDs → placeType for the selected trip
  const selectedTripPlaceMap = useMemo(
    () => new Map((selectedTrip?.places ?? []).map((p) => [p.item.id, p.placeType])),
    [selectedTrip],
  );

  // Build the per-day coordinate sequences from the trip. Stable key prevents refetching
  // when the trip object reference changes but scheduling didn't.
  const dayCoords = useMemo<Array<{ day: number; coords: Array<[number, number]> }>>(() => {
    if (!selectedTrip) return [];
    const byDay = new Map<number, Array<TripPlace>>();
    for (const p of selectedTrip.places) {
      if (p.scheduledDay == null) continue;
      const list = byDay.get(p.scheduledDay) ?? [];
      list.push(p);
      byDay.set(p.scheduledDay, list);
    }
    const result: Array<{ day: number; coords: Array<[number, number]> }> = [];
    for (const [day, places] of byDay) {
      if (places.length < 2) continue;
      const sorted = [...places].sort((a, b) => {
        // Lodging first (anchors the day), then by time, then by addedAt
        if (a.placeType === 'lodging' && b.placeType !== 'lodging') return -1;
        if (b.placeType === 'lodging' && a.placeType !== 'lodging') return 1;
        if (a.scheduledTime && b.scheduledTime)
          return a.scheduledTime.localeCompare(b.scheduledTime);
        if (a.scheduledTime) return -1;
        if (b.scheduledTime) return 1;
        return a.addedAt - b.addedAt;
      });
      result.push({
        day,
        coords: sorted.map((p) => [p.item.longitude, p.item.latitude]),
      });
    }
    return result.sort((a, b) => a.day - b.day);
  }, [selectedTrip]);

  // Fetch real driving routes per day from the worker (proxies Mapbox + KV-caches).
  // The `fetchDirections` helper dedupes identical requests in-memory, so even if the
  // effect re-runs more often than strictly necessary, no extra network calls happen.
  const [tripRouteGeoJson, setTripRouteGeoJson] = useState<GeoJSON.FeatureCollection | null>(null);

  useEffect(() => {
    if (dayCoords.length === 0) {
      setTripRouteGeoJson(null);
      return;
    }
    let cancelled = false;
    void Promise.all(
      dayCoords.map(({ day, coords }) =>
        fetchDirections(coords).then((geometry) => (geometry ? { day, geometry } : null)),
      ),
    ).then((results) => {
      if (cancelled) return;
      const features: Array<GeoJSON.Feature> = results
        .filter((r): r is { day: number; geometry: DirectionsLineString } => r !== null)
        .map(({ day, geometry }) => ({
          type: 'Feature',
          properties: { day },
          geometry,
        }));
      setTripRouteGeoJson(features.length > 0 ? { type: 'FeatureCollection', features } : null);
    });
    return () => {
      cancelled = true;
    };
  }, [dayCoords]);

  // Trip places that are NOT in the full (unfiltered) search results — shown as extra markers.
  // We use allItems here so category-filtered places don't incorrectly appear as "extras".
  const allItemIds = useMemo(() => new Set(allItems.map((i) => i.id)), [allItems]);
  const extraTripPlaces = useMemo(
    () => (selectedTrip?.places ?? []).filter((p) => !allItemIds.has(p.item.id)),
    [selectedTrip, allItemIds],
  );

  const handleMoveEnd = () => {
    // Re-cluster for the new viewport on EVERY settle — including programmatic
    // flyTo / cluster-expansion — so bubbles merge/split correctly.
    syncClusterView();
    if (isProgrammaticMoveRef.current) {
      isProgrammaticMoveRef.current = false;
      return;
    }
    if (travelMode) return;
    setShowSearchHere(true);
  };

  const handleSearchThisArea = () => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    void setLat(center.lat);
    void setLng(center.lng);
    void setRadius(zoomToRadius(zoom));
    setShowSearchHere(false);
  };

  // "Center map on trip" — flies to the selected trip's stored
  // centerpoint. Hidden when no trip is selected (or the trip has no
  // location, e.g. a brand-new trip the user hasn't picked a base for).
  const handleCenterOnTrip = useCallback(() => {
    if (selectedTrip?.lat == null || selectedTrip?.lng == null) return;
    isProgrammaticMoveRef.current = true;
    mapRef.current?.flyTo({
      center: [selectedTrip.lng, selectedTrip.lat],
      zoom: radiusToZoom(radiusMiles),
      duration: 500,
    });
  }, [selectedTrip, radiusMiles]);

  // "Locate me" — flies to the user's current position via the
  // browser Geolocation API. Replaces Mapbox's GeolocateControl
  // (which couldn't be visually separated from NavigationControl
  // without overlap). We don't render a persistent blue dot — kept
  // the affordance minimal.
  const handleLocateMe = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        isProgrammaticMoveRef.current = true;
        mapRef.current?.flyTo({
          center: [pos.coords.longitude, pos.coords.latitude],
          zoom: 13,
          duration: 500,
        });
      },
      () => {
        /* user denied or geolocation failed — nothing else to do */
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }, []);

  // Memoized callbacks — feed stable references to memoized `MapMarker`
  // children so they only re-render when their own props change.
  const handleMarkerClick = useCallback(
    (id: string) => void setSelectedItemId((current) => (id === current ? null : id)),
    [setSelectedItemId],
  );
  const handleMarkerHover = useCallback((id: string) => setHoveredItemId(id), []);
  const handleMarkerUnhover = useCallback(() => setHoveredItemId(null), []);

  // All items to render as markers (search results + extra trip places
  // not in results). Stable order — DO NOT re-sort by selection. The
  // marker variants both set `style.zIndex` based on `isSelected`
  // (heart pins: 50 base + 50 when selected; circular pins: 100 when
  // selected, auto otherwise), so the selected pin already paints on
  // top via z-index. An earlier version moved the selected item to the
  // end of the array for "paint last = paint on top", but reordering
  // the keyed list caused React to issue `insertBefore` calls — which
  // in turn caused the `kawaii-pin-drop` entrance animation to replay
  // on every marker that shifted position. That looked like "all pins
  // reload" on every click.
  const allMarkerItems = useMemo<Array<{ item: ItineraryItem; isExtra: boolean }>>(
    () => [
      ...items.map((item) => ({ item, isExtra: false })),
      ...extraTripPlaces.map((tp) => ({ item: tp.item, isExtra: true })),
    ],
    [items, extraTripPlaces],
  );

  // Trip-place hearts are always rendered individually (never clustered) — few
  // in number, and they're the user's own itinerary. Everything else (the up-to-
  // thousands of search-result pins) goes through supercluster so a dense area
  // paints a few count-bubbles instead of thousands of DOM marker nodes — the
  // fix for the "typing is slow everywhere" jank (2000+ marker DOM nodes made
  // every style recalc / paint expensive, worst on mobile).
  const tripLeaves = useMemo(
    () => allMarkerItems.filter((m) => selectedTripPlaceMap.has(m.item.id)),
    [allMarkerItems, selectedTripPlaceMap],
  );
  // The selected place is exempt from clustering too, so clicking a pin is
  // never answered by a count bubble sitting where that pin should be. See
  // `isNeverClustered` for why this is a partition rather than a zoom tweak.
  const clusterableItems = useMemo(
    () =>
      allMarkerItems.filter(
        (m) => !isNeverClustered(m.item.id, selectedTripPlaceMap, selectedItemId),
      ),
    [allMarkerItems, selectedTripPlaceMap, selectedItemId],
  );
  // Rendered separately from `tripLeaves` rather than merged into it: a search
  // pin keeps search-pin behaviour (draggable onto the trip), which the trip
  // hearts deliberately don't have. Null when the selection is a trip place
  // (already drawn as a heart) or isn't on the map at all.
  const selectedSearchLeaf = useMemo(
    () =>
      selectedItemId && !selectedTripPlaceMap.has(selectedItemId)
        ? (allMarkerItems.find((m) => m.item.id === selectedItemId) ?? null)
        : null,
    [allMarkerItems, selectedTripPlaceMap, selectedItemId],
  );
  const clusterItemById = useMemo(
    () => new Map(clusterableItems.map((m) => [m.item.id, m.item])),
    [clusterableItems],
  );
  const clusterer = useMemo(
    () =>
      createClusterer(
        clusterableItems.map((m) => ({
          id: m.item.id,
          longitude: m.item.longitude,
          latitude: m.item.latitude,
        })),
        { maxZoom: CLUSTER_MAX_ZOOM },
      ),
    [clusterableItems],
  );
  const renderedClusterPoints = useMemo(
    () => (clusterView ? clusterer.pointsIn(clusterView.bbox, clusterView.zoom) : []),
    [clusterer, clusterView],
  );
  const handleClusterExpand = useCallback(
    (clusterId: number, longitude: number, latitude: number) => {
      isProgrammaticMoveRef.current = true;
      mapRef.current?.flyTo({
        center: [longitude, latitude],
        zoom: Math.min(clusterer.expansionZoom(clusterId), 18),
        duration: 500,
      });
    },
    [clusterer],
  );

  const hoveredItem = hoveredItemId
    ? allMarkerItems.find((m) => m.item.id === hoveredItemId)?.item
    : null;

  return (
    <div ref={containerRef} className="flex-1 w-full h-full relative">
      <MapboxMap
        ref={mapRef}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_TOKEN}
        initialViewState={{ longitude: lng, latitude: lat, zoom: radiusToZoom(radiusMiles) }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={activeStyleId}
        onLoad={syncClusterView}
        onMoveEnd={handleMoveEnd}
        // Mapbox GL fires `contextmenu` on right-click AND on long-press
        // on touch devices, so one handler covers both. The parent owns
        // the modal — we just emit the lat/lng. preventDefault stops the
        // browser's native context menu from popping up over our UI.
        onContextMenu={(event) => {
          if (!onMapContextMenu) return;
          event.preventDefault();
          onMapContextMenu({
            latitude: event.lngLat.lat,
            longitude: event.lngLat.lng,
          });
        }}
      >
        <NavigationControl position="bottom-right" />

        {showRoutes && tripRouteGeoJson && (
          <Source id="trip-routes" type="geojson" data={tripRouteGeoJson}>
            <Layer
              id="trip-routes-line"
              type="line"
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{
                'line-color': '#7c3aed',
                'line-width': 4,
                'line-opacity': 0.7,
              }}
            />
          </Source>
        )}

        {/* Custom button column at top-right. Center-on-trip is gated on
            a selected trip with a stored location (otherwise there's
            nothing to recenter on). Locate-me is always visible. Both
            buttons share the same square white-card look so they read
            as siblings of the +/- zoom controls below. */}
        <div className="absolute top-1 right-1 z-content-overlay flex flex-col gap-1.5">
          {selectedTrip?.lat != null && selectedTrip?.lng != null && (
            <button
              type="button"
              onClick={handleCenterOnTrip}
              aria-label={`Center map on ${selectedTrip.name}`}
              title="Center map on trip"
              className="w-9 h-9 rounded-md bg-white border border-content-border shadow-md flex items-center justify-center text-content-subtle hover:text-primary-emphasized hover:bg-content-focal transition-colors"
            >
              <FontAwesomeIcon icon={faSuitcaseRolling} style={{ width: 14, height: 14 }} />
            </button>
          )}
          <button
            type="button"
            onClick={handleLocateMe}
            aria-label="Center map on my location"
            title="Center map on my location"
            className="w-9 h-9 rounded-md bg-white border border-content-border shadow-md flex items-center justify-center text-content-subtle hover:text-secondary-emphasized hover:bg-content-focal transition-colors"
          >
            <FontAwesomeIcon icon={faLocationCrosshairs} style={{ width: 14, height: 14 }} />
          </button>
        </div>

        {/* Map style switcher — hugs the bottom-left corner of the map.
            Uses z-content-overlay (50) so the page-level menu popover
            (z-menu = 200) renders on top when both are visible. The
            previous z-[150] tied with the header's z-navigation-sticky
            and let map buttons stack over the trip-menu dropdown. */}
        <div className="absolute bottom-1 left-1 z-content-overlay flex gap-1">
          {mapStyles.map((style) => (
            <button
              key={style.id}
              type="button"
              onClick={() => setActiveStyleId(style.id)}
              className={twx(
                'px-2.5 py-1 rounded-full text-xs font-semibold shadow-md border transition-all',
                activeStyleId === style.id
                  ? 'bg-white text-secondary-emphasized border-secondary-emphasized'
                  : 'bg-white/80 text-content-subtle border-content-border hover:bg-white hover:text-content-text',
              )}
            >
              {style.label}
            </button>
          ))}
        </div>

        {!travelMode && (
          // Always-visible search-area chip (three states — see SearchAreaChip).
          // z-content-overlay (50) keeps it above map markers (z-base 0) but
          // BELOW the header chrome + popovers (z-menu 200).
          <div className="absolute top-1 left-1 z-content-overlay pointer-events-none">
            <SearchAreaChip
              isLoading={isLoading}
              hasNewArea={showSearchHere}
              placeCount={items.length}
              onSearch={handleSearchThisArea}
            />
          </div>
        )}

        {/* Search-result pins, clustered. At low zoom a dense area collapses to
            a few count-bubbles; leaves (individual pins) reappear as you zoom in
            or expand a cluster — and only for the current viewport, so the DOM
            marker count stays in the tens, not the thousands. */}
        {renderedClusterPoints.map((point) => {
          if (point.kind === 'cluster') {
            return (
              <ClusterMarker
                key={`cluster-${point.clusterId}`}
                clusterId={point.clusterId}
                longitude={point.longitude}
                latitude={point.latitude}
                count={point.count}
                onExpand={handleClusterExpand}
              />
            );
          }
          const item = clusterItemById.get(point.id);
          if (!item) return null;
          return (
            <MapMarker
              key={item.id}
              item={item}
              isSelected={item.id === selectedItemId}
              tripPlaceType={null}
              onClick={handleMarkerClick}
              onHover={handleMarkerHover}
              onUnhover={handleMarkerUnhover}
              draggable={canDrag}
            />
          );
        })}

        {/* The selected search pin, held out of the cluster index so it's always
            visible. Same props as its clustered siblings above — only its
            exemption from grouping differs. */}
        {selectedSearchLeaf && (
          <MapMarker
            key={selectedSearchLeaf.item.id}
            item={selectedSearchLeaf.item}
            isSelected={true}
            tripPlaceType={null}
            onClick={handleMarkerClick}
            onHover={handleMarkerHover}
            onUnhover={handleMarkerUnhover}
            draggable={canDrag}
          />
        )}

        {/* Trip-place hearts — the user's itinerary, always individually visible
            (never clustered) and drawn above the search pins. */}
        {tripLeaves.map(({ item }) => (
          <MapMarker
            key={item.id}
            item={item}
            isSelected={item.id === selectedItemId}
            tripPlaceType={selectedTripPlaceMap.get(item.id) ?? null}
            onClick={handleMarkerClick}
            onHover={handleMarkerHover}
            onUnhover={handleMarkerUnhover}
            draggable={false}
          />
        ))}

        {/* Hover tooltip — suppressed when the item is already selected (detail
            panel shows). `anchor="bottom"` puts the bubble above the pin with
            its tip (arrow) pointing down at it. The offset is the tip-to-pin
            distance, sized so the arrow tip sits just above the hovered pin's
            top: circular search pins are ~28px (14px half-height), trip-place
            hearts ~44px (22px) — plus a few px of breathing room. The wrapper
            gets a high relative z-index so Mapbox marker stacking doesn't render
            it behind a neighboring trip pin. */}
        {hoveredItem && hoveredItem.id !== selectedItemId && (
          <Popup
            longitude={hoveredItem.longitude}
            latitude={hoveredItem.latitude}
            anchor="bottom"
            offset={selectedTripPlaceMap.has(hoveredItem.id) ? 25 : 17}
            closeButton={false}
            closeOnClick={false}
            className="[&_.mapboxgl-popup-content]:px-2 [&_.mapboxgl-popup-content]:py-1 [&_.mapboxgl-popup-content]:rounded-lg [&_.mapboxgl-popup-content]:shadow-lg [&_.mapboxgl-popup-content]:border [&_.mapboxgl-popup-content]:border-content-border [&_.mapboxgl-popup-tip]:border-t-white !z-[200]"
          >
            <p className="text-xs font-medium text-content-text max-w-[180px] truncate">
              {hoveredItem.name}
            </p>
          </Popup>
        )}
      </MapboxMap>
    </div>
  );
};

// Memoize the map shell. Even clustered, the Mapbox controls + marker set are
// non-trivial to re-render; this skips the work when AppInner re-renders for
// unrelated reasons (e.g., a modal toggle).
export const ItineraryMap = memo(ItineraryMapImpl);
